import { useEffect } from 'react'
import { useInView } from 'react-intersection-observer'
import styled, { useTheme } from 'styled-components'
import { Typo, TypoVariant } from '@/components/common/typography'
import { ScrollContainer } from '@/components/domains/etc'
import { NewProductCard } from '@/components/domains/products/new-product-card/new-product-card'
import { PRODUCT_CARD_SIZES } from '@/components/domains/products/new-product-card/new-product-card.type'
import SkeletonProduct from '@/components/domains/products/skeleton/SkeletonProduct'
import { PageSizeEnum } from '@/constants/page-size.enum'
import { ProductThumbnailSizeEnum } from '@/constants/product-thumbnail-size.enum'
import { useProductDetailEventTracker } from '@/containers/event-tracker/product-detail.event-tracker-new'
import useRelatedProductContainerHook from '@/containers/hooks/related-product-v2.container.hook'
import { GraphQLProductType, transformProductTypeDefinedByFrontend } from '@/utils/product/product-card.util'
import { decodeId, encodeId } from '@/utils/utilApi'

interface ProductDetailRelatedProductListProps {
  discountRate: number
  productId: number
  isRealSoldOut: boolean
  productName: string
  price: number
  salePrice: number
  reviewScore: number
  reviewCount: number
}

const RelatedProductListTitle = '다른 고객이 함께 구매한 상품'

/**
 * PDP 페이지에서 사용
 */
export const ProductDetailRelatedProductList = ({
  discountRate,
  productId,
  isRealSoldOut,
  productName,
  price,
  salePrice,
  reviewScore,
  reviewCount,
}: ProductDetailRelatedProductListProps) => {
  const { color } = useTheme()
  const { trackViewProductDetailBottomProductFirst, trackClickProductDetailBottomProduct } =
    useProductDetailEventTracker()
  const { ref, inView } = useInView({
    triggerOnce: true,
  })

  const { recommendedProductDetailProducts, loading: isLoading } = useRelatedProductContainerHook(
    encodeId('ProductType', productId),
    PageSizeEnum.PageSize20
  )

  const handleClickProductCard = ({ item, listIndex }: { item: GraphQLProductType; listIndex: number }) => {
    trackClickProductDetailBottomProduct({
      discountRate,
      productId,
      isRealSoldOut,
      productName,
      price,
      salePrice,
      reviewScore,
      reviewCount,
      listType: 'realtedproduct',
      listIndex,
      clickedProduct: {
        discountRate: item.discountRate,
        productId: +decodeId(item.id).id,
        isRealSoldOut: item.isRealSoldOut,
        productName: item.name,
        price: item.price,
        salePrice: item.customerPrice,
        reviewScore: item.averageReviewScore,
        reviewCount: item.reviewCount,
      },
    })
  }

  useEffect(() => {
    if (inView) {
      trackViewProductDetailBottomProductFirst({
        listType: 'withproduct',
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inView])

  if (!recommendedProductDetailProducts?.length) {
    return null
  }

  return (
    <RelatedProductListWrapper className="product-detail-related-product-list" ref={ref}>
      <div className="product-detail-related-product-list__header">
        <Typo variant={TypoVariant.Heading6Bold} color={color.gray900}>
          {RelatedProductListTitle}
        </Typo>
      </div>
      <ScrollContainer hideRightButton={recommendedProductDetailProducts.length < 3} buttonPositionY={30}>
        <div className="product-detail-related-product-list__list">
          {isLoading ? (
            <SkeletonProduct length={3} size={ProductThumbnailSizeEnum.Card136} />
          ) : (
            recommendedProductDetailProducts.map((product, index) => {
              const productData = transformProductTypeDefinedByFrontend(product)
              return (
                <div className="product-detail-related-product-list__item" key={productData.id}>
                  <NewProductCard
                    product={productData}
                    size={PRODUCT_CARD_SIZES.lg}
                    onClickProductCard={() => handleClickProductCard({ item: product, listIndex: index })}
                  />
                </div>
              )
            })
          )}
        </div>
      </ScrollContainer>
    </RelatedProductListWrapper>
  )
}

const RelatedProductListWrapper = styled.div`
  .product-detail-related-product-list {
    &__header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding: 0 1.6rem;
    }
    &__list {
      margin-top: 1.6rem;
      display: grid;
      grid-auto-flow: column;
      grid-template-columns: repeat(3, 1fr);
      grid-column-gap: 0.8rem;
      & > *:first-child {
        padding-left: 1.6rem;
      }
      & > *:last-child {
        padding-right: 1.6rem;
      }
    }
  }
`
